import type { ReactElement } from 'react';

import { Button } from '@/lib/base/buttons/button';

import type { CrmObject } from '../../core/types';
import type { HeaderActionDefinition } from '../registry/entityTypeRegistry';

export interface HeaderActionsProps {
  /** Action definitions from the entity-type registry (module-scope, stable reference). */
  actions: HeaderActionDefinition[];
  object: CrmObject;
}

/**
 * Renders entity-type header action buttons (Gmail / LinkedIn / Website, etc.).
 * Each action's href is computed from the object at render time; actions whose
 * `guard` fails or whose `getHref` returns null are not rendered.
 */
export function HeaderActions({ actions, object }: HeaderActionsProps) {
  return actions
    .map(action => {
      if (action.guard && !action.guard(object)) {
        return null;
      }
      const href = action.getHref(object);
      if (!href) {
        return null;
      }
      const Icon = action.icon;
      return (
        <Button
          key={action.id}
          size='xs'
          color='tertiary'
          href={href}
          target='_blank'
          rel='noopener noreferrer'
          iconLeading={Icon}
          aria-label={action.getLabel(object)}
          data-component='external-link-button'
          data-instance={action.id}
        />
      );
    })
    .filter((el): el is ReactElement => el !== null);
}
